import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { API_URL } from '../reusable/url';

import LoadingSpinner from './LoadingSpinner';

import './userInputForm.css';

const UserInputForm = ({ patientData }) => {
    const { 
        register, 
        handleSubmit, 
        formState: { errors, isValid }, 
        setValue 
    } = useForm({
        mode: 'onChange'
    });

    const [errorMessage, setErrorMessage] = useState('');
    const [pscore, setPscore] = useState(null);
    const [apProbability, setApProbability] = useState(null);
    const [metastasesProb, setMetastasesProb] = useState(null);
    const [pcsmProb, setPcsmProb] = useState(null);
    const [nccnRisk, setNccnRisk] = useState(null);
    const [percentile, setPercentile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const navigate = useNavigate();


        // Since preBiopsyPsa => "psa" and clinicalStage => "cTstage",
    // we can set them once we have patientData
    useEffect(() => {
        if (patientData) {
        // Use setValue to programmatically set form values so they are included when we submit
        setValue('psa', patientData.preBiopsyPsa || '');
        setValue('cTstage', patientData.clinicalStage || '');
        }
    }, [patientData, setValue]);

    const normalizeDecimalSeparator = (value) => {
        return value.replace(',', '.');
    };

    const onSubmit = async (data) => {
        const normalizedData = {
            ...data,
            psa: normalizeDecimalSeparator(data.psa),
            vgll3: normalizeDecimalSeparator(data.vgll3),
            f3: normalizeDecimalSeparator(data.f3),
            igfbp3: normalizeDecimalSeparator(data.igfbp3),
            cTstage: data.cTstage
        };

        // const isEmpty = Object.values(data).some(value => value === '');
        // if (isEmpty) {
        //     setErrorMessage('Please fill in all fields.');
        //     return;
        // }

        setIsSubmitting(true);

        try {
            
            const jwtToken = sessionStorage.getItem('token'); // always include JWT from frontend
            
            const response = await fetch(API_URL('calculate-pscore'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${jwtToken}` // important to pass JWT to Node backend
                },
                body: JSON.stringify(normalizedData),
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to calculate P-score');
            }
            const responseData = await response.json();

            // Ensure the entire response data is logged
            console.log('Full response data:', responseData);

            const { 
                pscore, 
                apProbability, 
                metastasesProb, 
                pcsmProb, 
                nccnRisk, 
                percentile 
            } = responseData;
            
            console.log('Setting states with:', { pscore, apProbability, metastasesProb, pcsmProb, nccnRisk, percentile });

            setPscore(pscore);
            setApProbability(apProbability);
            setMetastasesProb(metastasesProb);
            setPcsmProb(pcsmProb);
            setNccnRisk(nccnRisk);
            setPercentile(percentile)
            setErrorMessage('');
        } catch (error) {
            console.error('Error calculating P-score:', error);
            setErrorMessage('Failed to calculate P-score');
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (
            pscore !== null &&
            apProbability !== null && 
            metastasesProb !== null && 
            pcsmProb !== null && 
            nccnRisk !== null && 
            percentile !== null && 
            !isSubmitting
        ) {
            console.log('Navigating to report with data:', { pscore, apProbability, metastasesProb, pcsmProb, nccnRisk, percentile, formData: patientData });
            navigate('/report', { 
                state: { 
                    pscore, 
                    apProbability, 
                    metastasesProb, 
                    pcsmProb, 
                    nccnRisk, 
                    percentile, 
                    formData: patientData } });
        }
    }, [pscore, apProbability, metastasesProb, pcsmProb, nccnRisk, percentile, isSubmitting, navigate, patientData]);

    return (
        <div className="form-container">


            {isSubmitting ? (
                <LoadingSpinner /> // Show the spinner when submitting
            ) : (

            <form onSubmit={handleSubmit(onSubmit)}>
                {/* HIDDEN inputs for psa & cTstage so they do not appear again to the user */}
                <input
                    type="hidden"
                    {...register('psa', {
                    required: true, 
                    pattern: /^\d+([.,]\d+)?$/
                    })}
                />
                <input
                    type="hidden"
                    {...register('cTstage', {
                    required: true, 
                    pattern: /^[a-zA-Z0-9]+$/
                    })}
                />
                 {/* The rest of the user inputs that do NOT overlap with PatientForm */}


                <div className='total-user-input'>

                    <div className="input-container">
                        <input
                            id="cGleasonscore"
                            {...register('cGleasonscore', { required: true, pattern: /^\d+$/ })} 
                            type="text" placeholder=" " 
                        />
                        <label htmlFor="cGleasonscore">Total cGleasonscore</label>
                    </div>
                    {errors.cGleasonscore && <p className="input-error">Total cGleasonscore is required as integer.</p>}

                    <div className="input-container">
                        <input 
                            id="vgll3"
                            {...register('vgll3', { required: true, pattern: /^-?\d+([.,]\d+)?$/ })} 
                            type="text" placeholder=" " 
                        />
                        <label htmlFor="vgll3">VGLL3</label>
                    </div>
                    {errors.vgll3 && <p className="input-error">VGLL3 is required and must be a number.</p>}

                    <div className="input-container">
                        <input 
                            id="f3"
                            {...register('f3', { required: true, pattern: /^-?\d+([.,]\d+)?$/ })} 
                            type="text" placeholder=" " 
                        />
                        <label htmlFor="f3">F3</label>
                    </div>
                    {errors.f3 && <p className="input-error">F3 is required and must be a number.</p>}

                    <div className="input-container">
                        <input
                            id="igfbp3" 
                            {...register('igfbp3', { required: true, pattern: /^-?\d+([.,]\d+)?$/ })} 
                            type="text" placeholder=" " 
                        />
                        <label htmlFor='igfbp3'>IGFBP3</label>
                    </div>
                    {errors.igfbp3 && <p className="input-error">IGFBP3 is required and must be a number.</p>}
                </div>
                {/* 6) Toggle button color by isValid */}
                <button
                    type="submit"
                    className={`btn-submit-user-input-form ${isValid ? 'valid' : 'invalid'}`}
                    disabled={!isValid}
                >
                    Submit
                </button>

                {/* <button type="submit" className="btn-submit-user-input-form">Submit</button> */}
                {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
            </form>

            )}
        </div>
    );
};

export default UserInputForm;
